import { ImagePaths } from '../../lib/imageUrls.js';

export const securingActiveDirectory = {

    id: 33,
    slug: 'securing-active-directory',
    ogImage: '/og/courses/securing-active-directory.jpg',
    courseCode: 'AD-SEC-001',
    featured: false,
    title: 'Securing Active Directory — Practitioner Course',
    subtitle: 'Harden on-prem and hybrid Active Directory against modern identity attacks',
    valueProposition: 'Active Directory remains the single largest blast radius in most Australian enterprises. This practitioner course gives identity engineers a tested playbook to harden domain controllers, contain privileged access, detect Kerberos abuse, and rehearse forest recovery before an attacker forces the issue.',
    duration: '24 hours',
    students: 'New',
    level: 'Intermediate',
    image: ImagePaths.COURSE_2,
    shortDescription: 'AD-SEC-001 — Active Directory hardening covering tier model, LAPS, Kerberos detection, and forest recovery for Australian identity and security engineers.',
    price: {
      aud: 1299,
      currency: 'AUD',
      earlyBird: 999
    },
    stripeConfig: {
      productId: 'prod_Uars38lhBbMfuY',
      priceId: 'price_1TikaKKu1nyc322UcQx0AQzO'
    },

    snapshot: {
      whoItsFor: [
        'Active Directory and identity engineers responsible for on-prem and hybrid environments',
        'Security architects designing tiered administration and privileged access models',
        'IT managers accountable for identity resilience and Essential Eight Maturity Level 2 recovery controls',
        'Incident response and detection engineers building Kerberos and DCSync detections'
      ],
      accreditation: 'Aligned to ASD Essential Eight Maturity Level 2 (regular backups, restrict admin privileges) and ACSC Hardening Microsoft Windows guidance'
    },

    logistics: {
      classSize: 'Three-day intensive — live virtual cohort with hands-on labs',
      requirements: [
        'Working knowledge of Active Directory Domain Services, Group Policy and DNS',
        'Familiarity with PowerShell for administrative tasks',
        'Access to a lab AD forest (Azure VM or Hyper-V) for hands-on exercises',
        'Reading familiarity with Microsoft "Securing Privileged Access" reference material'
      ]
    },

    overview: 'Active Directory underpins authentication for the majority of Australian enterprise, government and critical infrastructure environments, yet most forests still run with flat administrative models, unmanaged service accounts and unverified backups. This three-day practitioner course walks identity engineers through the controls Microsoft, the ACSC and incident responders agree actually matter: the tier model and Enterprise Admins isolation, domain controller hardening (LAPS, NTLM removal, SMB signing), Privileged Access Workstations, Kerberos and Golden Ticket detection, AD backup verification and full forest recovery rehearsals, and the risk register for hybrid Entra cutovers.\n\nEvery module pairs Microsoft and ACSC reference guidance with current attacker tradecraft — Mimikatz, DCSync, Kerberoasting, AdminSDHolder abuse — so participants leave with a tested playbook and the evidence to brief executives on residual identity risk.',

    outcomes: [
      'Design and implement Microsoft\'s administrative tier model and isolate Enterprise Admins from daily operations',
      'Harden domain controllers using ACSC Windows hardening guidance, including SMB signing, LDAP signing and NTLM auditing then removal',
      'Deploy Windows LAPS to rotate local administrator credentials and eliminate shared-password lateral movement',
      'Build and operate Privileged Access Workstations (PAWs) for tier-0 administration',
      'Detect and respond to Kerberos abuse including Golden Ticket, Silver Ticket, Kerberoasting and DCSync (MITRE ATT&CK T1003.006)',
      'Verify Active Directory backups and rehearse a full forest recovery against Microsoft\'s documented procedure',
      'Build a hybrid AD-to-Entra cutover risk register covering password hash sync, seamless SSO, and synchronisation account exposure',
      'Map identity controls to ASD Essential Eight Maturity Level 2 and the ASD Information Security Manual (ISM)'
    ],

    audience: [
      'Active Directory and identity engineers',
      'Security architects and identity architects',
      'IT managers responsible for identity resilience',
      'Incident response and detection engineers',
      'Hybrid cloud engineers managing AD–Entra synchronisation'
    ],

    prerequisites: [
      'Working AD DS administration experience',
      'PowerShell scripting fluency',
      'Lab environment with at least two domain controllers'
    ],

    modules: [
      {
        number: 1,
        title: 'AD Tier Model and Enterprise Admins Isolation',
        duration: '4 hours',
        topics: [
          'Microsoft\'s tier 0/1/2 administrative model — what changed in the "Securing Privileged Access" reference (Microsoft, updated 2024)',
          'Removing standing Enterprise Admins and Domain Admins membership',
          'Time-bound elevation patterns using Privileged Identity Management and just-in-time groups',
          'AdminSDHolder, Protected Users group and authentication policy silos',
          'Mapping the tier model to the ACSC Essential Eight "restrict administrative privileges" control'
        ]
      },
      {
        number: 2,
        title: 'Hardening Domain Controllers — LAPS, NTLM Removal, SMB Signing',
        duration: '4 hours',
        topics: [
          'ACSC Hardening Microsoft Windows Workstations and Servers (2024 release, re-checked against cyber.gov.au each quarter) applied to domain controllers',
          'Deploying Windows LAPS (the in-box successor to legacy LAPS, GA April 2023) with Entra ID and on-prem AD backends',
          'Auditing then disabling NTLMv1 and constraining NTLMv2 — Microsoft\'s NTLM deprecation roadmap (announced October 2023)',
          'Enforcing SMB signing and SMB encryption — defaults changed in Windows Server 2025',
          'LDAP channel binding and signing requirements (Microsoft ADV190023, enforcement guidance as last updated by Microsoft)'
        ]
      },
      {
        number: 3,
        title: 'Privileged Access Workstations',
        duration: '4 hours',
        topics: [
          'PAW architecture per Microsoft\'s "Why are Privileged Access Workstations important" reference',
          'Hardware selection, secured-core PC requirements and Windows 11 baseline',
          'Application allow-listing on PAWs using Windows Defender Application Control',
          'Network segmentation, jump server patterns and credential isolation',
          'Operating model: provisioning, patching and decommissioning PAWs at scale'
        ]
      },
      {
        number: 4,
        title: 'Kerberos and Golden Ticket Detection',
        duration: '4 hours',
        topics: [
          'Kerberos attack tradecraft: Kerberoasting, AS-REP roasting, Golden Ticket, Silver Ticket, DCSync',
          'MITRE ATT&CK T1003 OS Credential Dumping — particularly T1003.006 DCSync detection signals',
          'krbtgt password rotation procedure and the two-rotation rule',
          'Detection sources: Windows Security event IDs 4624/4769/4662, Microsoft Defender for Identity, and SIEM queries',
          'Semperis Purple Knight and Specops research on AD exposure (Semperis "Identity Attack Weather Report" and Specops "Weak Password Report", latest published editions) — using public findings to prioritise hardening'
        ]
      },
      {
        number: 5,
        title: 'AD Backup Verification and Forest Recovery Rehearsals',
        duration: '4 hours',
        topics: [
          'Microsoft "AD Forest Recovery Guide" walkthrough (Microsoft Learn revision, re-checked each quarter)',
          'System state backups, IFM media and offline domain controller restore',
          'Tabletop and live-fire forest recovery rehearsals — what "tested backups" means under Essential Eight Maturity Level 2',
          'Rebuilding trust, DNS, FSMO roles and metadata cleanup after a destructive incident',
          'Evidence pack for auditors: ASD ISM control mapping for backup integrity and recovery'
        ]
      },
      {
        number: 6,
        title: 'Hybrid AD to Entra Cutover Risk Register',
        duration: '4 hours',
        topics: [
          'Entra Connect Sync vs Entra Cloud Sync — Microsoft guidance as currently published, and the deprecation of legacy Azure AD Connect',
          'Synchronisation account hardening — the historic MSOL_ account abuse pattern and current mitigations',
          'Password Hash Sync, Pass-through Authentication and Seamless SSO threat model',
          'Building a cutover risk register: identity blast radius, rollback plan, break-glass accounts, Conditional Access fail-open scenarios',
          'Decommissioning on-prem AD safely once workloads are Entra-joined'
        ]
      }
    ],

    format: {
      type: 'Three-day live virtual cohort with hands-on labs',
      includes: [
        '24 hours of practitioner instruction, roughly 60 per cent of it lab work',
        'Provided lab AD forest for LAPS rollout, NTLM auditing and Kerberos attack simulation',
        'Full forest recovery rehearsal against Microsoft\'s documented procedure',
        'Tier model design templates and Privileged Access Workstation build standard',
        'Kerberos and DCSync detection query pack for Windows event logs and SIEM',
        'Hybrid AD-to-Entra cutover risk register template',
        'Auditor evidence pack mapping controls to ASD ISM and Essential Eight Maturity Level 2',
        'Certificate of completion with CPD hours'
      ]
    },

    nextIntake: {
      date: 'Register your interest — we will confirm the next cohort',
      details: 'Three-day cohorts run every six weeks and are commonly booked ahead of Detecting Active Directory Compromise. Private cohorts can be scheduled against your own change windows'
    },

    certification: 'Educ4te Certificate of Completion — Securing Active Directory (Practitioner). Includes CPD hours suitable for ACS, ISACA and (ISC)² claims.',

    faqs: [
      {
        question: 'Is this course relevant if we are migrating to Entra ID and want to retire on-prem AD?',
        answer: 'Yes — Module 6 is built around hybrid cutover risk. Even in a "cloud-first" plan, on-prem AD typically remains the source of truth for years, and the synchronisation account is itself a tier-0 asset. The course helps you reduce on-prem blast radius while the migration runs.'
      },
      {
        question: 'How much of the course is hands-on?',
        answer: 'Roughly 60 per cent of class time is lab work in a provided AD forest, including LAPS rollout, NTLM auditing, Kerberos attack simulation, krbtgt rotation and a full forest recovery rehearsal. Theory is paired with vendor and ACSC reference material rather than slide-only delivery.'
      },
      {
        question: 'Does this map to ASD Essential Eight Maturity Level 2?',
        answer: 'Yes. The course explicitly maps controls to "restrict administrative privileges" and "regular backups" at Maturity Level 2, with reference to current ASD Essential Eight Assessment Process Guide language. We also reference the ASD Information Security Manual identity controls.'
      },
      {
        question: 'Do I need expensive third-party tooling like Microsoft Defender for Identity?',
        answer: 'No — the course shows what is achievable with Windows event logs, native PowerShell, Windows LAPS and Group Policy alone, then explains where Defender for Identity, Semperis and similar tools add value. You leave with a roadmap that fits your existing budget.'
      },
      {
        question: 'How current is the threat content?',
        answer: 'The course references the durable milestones that still shape Active Directory hardening — Windows LAPS reaching general availability in April 2023, Microsoft announcing the NTLM deprecation roadmap in October 2023, the SMB signing defaults that shipped with Windows Server 2025 — alongside the current Semperis and Specops annual research. Kerberos tradecraft and the detection content are re-verified against Microsoft Learn, ACSC advisories and the latest published research each quarter, and the reference list is updated with them.'
      },
      {
        question: 'Is there a group rate for our identity team?',
        answer: 'Yes — discounted seats are available for teams of four or more, and we can run a private cohort against your own change windows. Contact the Educ4te team for a quote.'
      }
    ],

    australianContext: true,
    complianceFrameworks: [
      'ASD Essential Eight (Maturity Level 2 — regular backups, restrict administrative privileges)',
      'ASD Information Security Manual (ISM)',
      'ACSC Hardening Microsoft Windows Workstations and Servers',
      'Microsoft Securing Privileged Access reference',
      'MITRE ATT&CK T1003 (OS Credential Dumping, including T1003.006 DCSync)'
    ],
};
